import diagonalArrow from '@/assets/images/Diagonal Arrow-01.png';
import logo from '@/assets/images/Growdash FINAL LOGO-02 (1).png';
import translateURL from '@/lib/translateUrl';
import { cn } from '@/lib/utils';
import { LanguageCode } from '@/types/language';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@growdash/design-system/atoms';
import { Link, useLocation, useNavigate } from '@remix-run/react';
import { motion } from 'framer-motion';
import {
  ChevronDown,
  ChevronUp,
  Languages,
  LucideIcon,
  Menu
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from './sheet';

interface NavItem {
  name: string;
  url: string;
  icon: LucideIcon;
}

interface NavBarProps {
  items: NavItem[];
  languages: { code: string; name: string }[];
}

export function NavBar({ items, languages }: NavBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [isMobileLangOpen, setMobileLangOpen] = useState(false);

  function removeLanguagePrefix(pathname: string) {
    const segments = pathname.split('/').filter(Boolean);
    const supportedLanguages = new Set(languages.map(lang => lang.code));
    if (segments[0] && supportedLanguages.has(segments[0])) {
      segments.shift();
    }
    return `/${segments.join('/')}`;
  }

  const normalizedPath = removeLanguagePrefix(location.pathname);
  const activeItem = items.find(item => item.url === normalizedPath);
  const segments = location.pathname.split('/');
  const currentLocale =
    languages.find(lang => lang.code === segments[1])?.code || LanguageCode.EN;

  // Determine if the current language is RTL (e.g., Arabic)
  const isRTL = currentLocale === LanguageCode.AR;
  // Use 'start' for RTL and 'end' for LTR alignment.
  const dropdownAlign = isRTL ? 'start' : 'end';

  // Language change process
  const handleLanguageChange = async (lang: string) => {
    try {
      // Persist i18next setting
      localStorage.setItem('i18nextLng', lang);
      await i18n.changeLanguage(lang);

      // 1) If we're on a blog-detail page, always go back to /blog
      const normalized = removeLanguagePrefix(location.pathname);
      // matches "/blog/anything" but NOT "/blog" itself
      if (/^\/blog\/[^/]+$/.test(normalized)) {
        const target = lang === LanguageCode.EN ? '/blog' : `/${lang}/blog`;
        navigate(target);
        return;
      }

      if (/^\/page\/[^/]+$/.test(normalizedPath)) {
        const target = lang === LanguageCode.EN ? '/' : `/${lang}`;
        navigate(target);
        return;
      }

      // 2) Otherwise, follow your existing prefix-shift logic
      const segs = location.pathname.split('/').filter(Boolean);
      const supported = new Set(languages.map(l => l.code));

      if (segs.length && supported.has(segs[0])) {
        // already has a lang prefix
        if (lang === LanguageCode.EN) {
          segs.shift();
        } else {
          segs[0] = lang;
        }
        const next = `/${segs.join('/')}`;
        navigate(next);
      } else if (lang !== LanguageCode.EN) {
        // no prefix, switching to non-en
        navigate(translateURL(lang, location.pathname));
      } else {
        // switching to English, stay on path
        navigate(location.pathname, { replace: true });
      }
    } catch (error) {
      console.error('Language switch error:', error); // eslint-disable-line no-console
    }
  };

  return (
    <div className='fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-7xl'>
      <div className='bg-white/80 backdrop-blur-sm rounded-lg border shadow-sm'>
        <nav className='flex items-center justify-between h-16 px-6'>
          {/* Logo */}
          <Link to={translateURL(currentLocale, '/')}>
            <div className='flex items-center gap-2'>
              <img
                src={diagonalArrow}
                alt={t('navbar.logoSmallAlt')}
                className='w-8 h-8 md:hidden'
              />
              <div className='hidden md:flex items-center gap-1'>
                <img
                  src={logo}
                  alt={t('navbar.logoAlt')}
                  className='h-[35px]'
                />
              </div>
            </div>
          </Link>

          {/* Navigation Items - Desktop */}
          <div className='hidden md:flex items-center gap-6'>
            {items.map(item => {
              const isActive = activeItem?.name === item.name;
              return (
                <Link
                  key={item.name}
                  to={translateURL(currentLocale, item.url)}
                  className={cn(
                    'relative text-sm font-medium transition-colors',
                    'text-gray-600 hover:text-gray-900',
                    isActive && 'text-primary'
                  )}
                >
                  {item.name}
                  {isActive && (
                    <motion.div
                      layoutId='nav-indicator'
                      className='absolute -bottom-1 left-0 right-0 h-0.5 bg-primary'
                      initial={false}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30
                      }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Auth Buttons & Language Dropdown */}
          <div className='flex items-center gap-4'>
            <div className='hidden md:flex items-center gap-4'>
              <Button
                variant='ghost'
                className='text-gray-600 hover:text-gray-900'
              >
                <a
                  href='https://dashboard.mygrowdash.com/'
                  target='_blank'
                  rel='noreferrer'
                >
                  {t('navbar.signIn')}
                </a>
              </Button>
              <Button
                style={{ background: '#060081' }}
                className='text-white hover:bg-[#060081]/90'
              >
                <Link to={translateURL(currentLocale, '/book-demo')}>
                  {t('navbar.cta')}
                </Link>
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='icon'>
                    <Languages className='!h-6 !w-6 text-[#060081]' />
                    <span className='sr-only'>
                      {t('navbar.selectLanguage')}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={dropdownAlign}>
                  {languages.map(lang => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className='w-full ms-3'
                    >
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile Menu */}
            <div className='md:hidden'>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant='ghost' size='icon'>
                    <Menu className='!h-6 !w-6 text-[#060081]' />
                    <span className='sr-only'>{t('navbar.toggleMenu')}</span>
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>{t('navbar.menu')}</SheetTitle>
                  </SheetHeader>
                  <div className='flex flex-col gap-4 mt-6 text-center'>
                    {items.map(item => {
                      const isActive = activeItem?.name === item.name;
                      return (
                        <SheetClose asChild key={item.name}>
                          <Link
                            to={translateURL(currentLocale, item.url)}
                            className={cn(
                              'text-lg font-medium transition-colors',
                              'text-gray-600 hover:text-gray-900',
                              isActive && 'text-primary'
                            )}
                          >
                            {item.name}
                          </Link>
                        </SheetClose>
                      );
                    })}
                    <div className='mt-4 border-t pt-4'>
                      <button
                        onClick={() => setMobileLangOpen(!isMobileLangOpen)}
                        className='flex items-center justify-center gap-2 w-full text-sm font-medium text-gray-600'
                      >
                        {t('navbar.selectLanguage')}
                        {isMobileLangOpen ? (
                          <ChevronUp className='h-5 w-5' />
                        ) : (
                          <ChevronDown className='h-5 w-5' />
                        )}
                      </button>
                      {isMobileLangOpen && (
                        <div className='flex flex-col justify-center items-center gap-2 mt-2'>
                          {languages.map(lang => (
                            <SheetClose asChild key={lang.code}>
                              <button
                                onClick={() => handleLanguageChange(lang.code)}
                                className='text-lg font-medium transition-colors text-gray-600 hover:text-gray-900'
                              >
                                {lang.name}
                              </button>
                            </SheetClose>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className='flex flex-col gap-2 mt-4'>
                      <SheetClose asChild>
                        <Button
                          variant='ghost'
                          className='w-full justify-start text-gray-600 hover:text-gray-900'
                        >
                          <a
                            href='https://dashboard.mygrowdash.com/'
                            target='_blank'
                            rel='noreferrer'
                          >
                            {t('navbar.signIn')}
                          </a>
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button
                          style={{ background: '#060081' }}
                          className='w-full text-white hover:bg-[#060081]/90'
                        >
                          <Link to={translateURL(currentLocale, '/book-demo')}>
                            {t('navbar.getStarted')}
                          </Link>
                        </Button>
                      </SheetClose>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
}
